/**
 * features/onboarding/onboarding-api — Mock provision endpoint for B1.2.
 *
 * Authority: ADR-108 §2 API Contract.
 *
 * This mock simulates the server-side single-transaction provisioning:
 *   1. Resolve subVerticalKey → BusinessProfile metadata
 *   2. Check email / tax_id uniqueness against local store
 *   3. Generate UUIDs for the 6-row insert cascade
 *   4. Persist a `TenantMembership` + AuthUser to the shared store
 *   5. Mint a mock JWT (base64 of {sub, email, role, exp})
 *
 * Signature is API-shape compatible so the switch to the real endpoint is
 * a one-line body swap (`apiPost('/auth/business/onboarding/provision', ...)`).
 *
 * Idempotency:
 *   Same `Idempotency-Key` returns the cached response from the mock store
 *   (48h retention matches ADR-022 baseline; LocalStorage-backed).
 */
import type { TenantMembership } from '@/shared/context/TenantContext';
import type { AuthUser } from '@/shared/hooks/useAuth';
import { findSubVertical } from './sub-verticals';
import type {
  OnboardingErrorCode,
  OnboardingFieldViolation,
  ProvisionTenantRequest,
  ProvisionTenantResponse,
} from './types';

const STORE_KEY = 'petota-business:onboarding-mock-store';
const IDEMPOTENCY_KEY = 'petota-business:onboarding-idempotency';
const LATENCY_MS = 420;

interface MockStore {
  users: Array<{ id: string; email: string; passwordHash: string; displayName: string; platformRole: AuthUser['platformRole'] }>;
  legalEntities: Array<{ id: string; taxId: string; countryCode: string; displayName: string; userId: string; createdAt: string }>;
  branches: Array<{ id: string; legalEntityId: string; branchName: string; createdAt: string }>;
  verticalCapabilities: Array<{ id: string; branchId: string; subVerticalKey: string }>;
  staffMembers: Array<{ id: string; userId: string; branchId: string; verticalCapabilityId: string; role: TenantMembership['role'] }>;
}

interface IdempotencyCache {
  [key: string]: {
    requestHash: string;
    response: ProvisionTenantResponse;
    createdAt: string;
  };
}

function seedStore(): MockStore {
  return { users: [], legalEntities: [], branches: [], verticalCapabilities: [], staffMembers: [] };
}

function readStore(): MockStore {
  if (typeof window === 'undefined') return seedStore();
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return seedStore();
    return { ...seedStore(), ...(JSON.parse(raw) as Partial<MockStore>) };
  } catch {
    return seedStore();
  }
}

function writeStore(store: MockStore): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

function readIdempotency(): IdempotencyCache {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(IDEMPOTENCY_KEY);
    return raw ? (JSON.parse(raw) as IdempotencyCache) : {};
  } catch {
    return {};
  }
}

function writeIdempotency(cache: IdempotencyCache): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(IDEMPOTENCY_KEY, JSON.stringify(cache));
}

async function hashPayload(body: ProvisionTenantRequest): Promise<string> {
  const { password: _pw, ...accountRest } = body.account;
  const canonical = JSON.stringify({ ...body, account: accountRest });
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const buf = new TextEncoder().encode(canonical);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }
  let hash = 0;
  for (let i = 0; i < canonical.length; i += 1) {
    hash = (hash * 31 + canonical.charCodeAt(i)) | 0;
  }
  return `fallback-${hash}`;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function base64Url(input: string): string {
  if (typeof btoa === 'undefined') {
    return Buffer.from(input, 'utf-8').toString('base64url');
  }
  return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function mintMockJwt(user: AuthUser, branchId: string): { token: string; expiresAt: string } {
  const header = base64Url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const expUnix = Math.floor(Date.now() / 1000) + 30 * 60;
  const expIso = new Date(expUnix * 1000).toISOString();
  const payload = base64Url(
    JSON.stringify({
      sub: user.id,
      email: user.email,
      platformRole: user.platformRole,
      branchId,
      exp: expUnix,
    }),
  );
  return { token: `${header}.${payload}.mock`, expiresAt: expIso };
}

function pseudoHash(password: string): string {
  return `mock$${password.length}$${base64Url(password).slice(0, 8)}`;
}

function delay<T>(value: T, ms = LATENCY_MS): Promise<T> {
  return new Promise(resolve => setTimeout(() => resolve(value), ms));
}

export class OnboardingApiError extends Error {
  constructor(
    public readonly code: OnboardingErrorCode,
    public readonly status: number,
    public readonly violations: OnboardingFieldViolation[] = [],
  ) {
    super(code);
    this.name = 'OnboardingApiError';
  }
}

export interface ProvisionTenantOptions {
  idempotencyKey: string;
}

export async function provisionTenant(
  body: ProvisionTenantRequest,
  { idempotencyKey }: ProvisionTenantOptions,
): Promise<ProvisionTenantResponse> {
  if (!idempotencyKey) {
    throw new OnboardingApiError('provisioning_failed', 503);
  }

  const cache = readIdempotency();
  const hash = await hashPayload(body);

  const existing = cache[idempotencyKey];
  if (existing) {
    if (existing.requestHash !== hash) {
      throw new OnboardingApiError('idempotency_conflict', 409);
    }
    return delay(existing.response, 120);
  }

  const subVertical = findSubVertical(body.business.subVerticalKey);
  if (!subVertical) {
    throw new OnboardingApiError('sub_vertical_not_found', 422, [
      { path: 'business.subVerticalKey', code: 'unknown_key', message: 'unknown_key' },
    ]);
  }

  if (!body.account.acceptTerms) {
    throw new OnboardingApiError('terms_not_accepted', 422, [
      { path: 'account.acceptTerms', code: 'required', message: 'required' },
    ]);
  }

  const store = readStore();

  const emailLower = body.account.email.toLowerCase();
  if (store.users.some(u => u.email === emailLower)) {
    throw new OnboardingApiError('email_already_exists', 409, [
      { path: 'account.email', code: 'duplicate', message: 'duplicate' },
    ]);
  }
  if (store.legalEntities.some(e => e.taxId === body.business.taxId)) {
    throw new OnboardingApiError('tax_id_already_exists', 409, [
      { path: 'business.taxId', code: 'duplicate', message: 'duplicate' },
    ]);
  }

  const userId = uuid();
  const legalEntityId = uuid();
  const branchId = uuid();
  const verticalCapabilityId = uuid();
  const staffId = uuid();
  const nowIso = new Date().toISOString();

  store.users.push({
    id: userId,
    email: emailLower,
    passwordHash: pseudoHash(body.account.password),
    displayName: body.account.displayName.trim(),
    platformRole: 'business_owner',
  });
  store.legalEntities.push({
    id: legalEntityId,
    taxId: body.business.taxId,
    countryCode: body.business.countryCode,
    displayName: body.business.businessName.trim(),
    userId,
    createdAt: nowIso,
  });
  store.branches.push({
    id: branchId,
    legalEntityId,
    branchName: body.branch.branchName.trim(),
    createdAt: nowIso,
  });
  store.verticalCapabilities.push({
    id: verticalCapabilityId,
    branchId,
    subVerticalKey: body.business.subVerticalKey,
  });
  store.staffMembers.push({
    id: staffId,
    userId,
    branchId,
    verticalCapabilityId,
    role: 'owner',
  });
  writeStore(store);

  const user: AuthUser = {
    id: userId,
    fullName: body.account.displayName.trim(),
    actorType: 'INDIVIDUAL',
    avatarUrl: null,
    lastLoginAt: null,
    displayName: body.account.displayName.trim(),
    email: emailLower,
    platformRole: 'business_owner',
  };

  const membership: TenantMembership = {
    organizationId: legalEntityId,
    organizationName: body.business.businessName.trim(),
    legalEntityId,
    legalEntityName: body.business.businessName.trim(),
    branchId,
    branchName: body.branch.branchName.trim(),
    businessProfileKey: body.business.subVerticalKey,
    verticalCapabilities: [body.business.subVerticalKey],
    role: 'owner',
  };

  const auth = mintMockJwt(user, branchId);

  const response: ProvisionTenantResponse = {
    tenant: {
      legalEntityId,
      branchId,
      verticalCapabilityId,
      subVerticalKey: body.business.subVerticalKey,
      businessProfileVersion: '1.0.0',
    },
    user,
    membership,
    auth,
    onboardingFlags: {
      emailVerificationSent: true,
      defaultServicesSeedQueued: true,
    },
  };

  cache[idempotencyKey] = {
    requestHash: hash,
    response,
    createdAt: nowIso,
  };
  writeIdempotency(cache);

  return delay(response);
}

export function resetOnboardingMockStore(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORE_KEY);
  window.localStorage.removeItem(IDEMPOTENCY_KEY);
}
